// SPDX-License-Identifier: AGPL-3.0-or-later

import {Routes} from '@app/app/Routes';
import styles from '@app/features/app/components/layout/NativeTitlebar.module.css';
import {PREMIUM_PRODUCT_NAME} from '@app/features/app/config/I18nDisplayConstants';
import {GuildIcon} from '@app/features/guild/components/popouts/GuildIcon';
import Guilds from '@app/features/guild/state/Guilds';
import {DIRECT_MESSAGES_DESCRIPTOR, FAVORITES_DESCRIPTOR} from '@app/features/i18n/utils/CommonMessageDescriptors';
import {LOBBY_COPY} from '@app/features/lobby/utils/LobbyCopy';
import {useLocation} from '@app/features/platform/components/router/RouterReact';
import {SOCIAL_COPY} from '@app/features/social/utils/SocialDiscoverCopy';
import {STORE_COPY} from '@app/features/store/utils/StoreCopy';
import {Avatar} from '@app/features/ui/components/Avatar';
import {LafonLoIcon} from '@app/features/ui/components/icons/LafonLoIcon';
import Users from '@app/features/user/state/Users';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {
	AtIcon,
	BellIcon,
	BookmarkSimpleIcon,
	CompassIcon,
	GameControllerIcon,
	SparkleIcon,
	StarIcon,
	StorefrontIcon,
} from '@phosphor-icons/react';
import {observer} from 'mobx-react-lite';
import type React from 'react';

const EXPLORE_COMMUNITIES_DESCRIPTOR = msg({
	message: 'Explore communities',
	comment: 'Short label in the sidebar navigation discovery button.',
});
const BOOKMARKS_DESCRIPTOR = msg({
	message: 'Bookmarks',
	comment: 'Short label in the saved messages page. Keep it concise.',
});
const RECENT_MENTIONS_DESCRIPTOR = msg({
	message: 'Recent mentions',
	comment: 'Short label in the recent mentions page. Keep it concise.',
});
const UNREADS_DESCRIPTOR = msg({
	message: 'Unreads',
	comment: 'Short label in the notifications page. Keep it concise.',
});

export function isAppContextTitlebarPath(pathname: string): boolean {
	return (
		Routes.isChannelRoute(pathname) ||
		pathname === Routes.BOOKMARKS ||
		pathname === Routes.MENTIONS ||
		pathname === Routes.NOTIFICATIONS ||
		pathname === Routes.YOU ||
		pathname === Routes.PREMIUM ||
		pathname === Routes.STORE ||
		pathname.startsWith('/discover') ||
		pathname.startsWith('/lobbies') ||
		pathname.startsWith('/users/')
	);
}

function PageMark({children}: {children: React.ReactNode}) {
	return <span className={styles.context}>{children}</span>;
}

export const AppTitlebarContext = observer(function AppTitlebarContext() {
	const {i18n} = useLingui();
	const {pathname} = useLocation();

	if (Routes.isGuildChannelRoute(pathname)) {
		const guildId = pathname.split('/')[2];
		const guild = guildId ? Guilds.getGuild(guildId) : undefined;
		if (!guild) return null;
		return (
			<PageMark>
				<GuildIcon id={guild.id} name={guild.name} icon={guild.icon} sizePx={16} className={styles.contextGuildIcon} />
				<span className={styles.contextLabel}>{guild.name}</span>
			</PageMark>
		);
	}

	if (Routes.isFavoritesRoute(pathname)) {
		return (
			<PageMark>
				<StarIcon className={styles.contextIcon} weight="fill" />
				<span className={styles.contextLabel}>{i18n._(FAVORITES_DESCRIPTOR)}</span>
			</PageMark>
		);
	}

	if (Routes.isDiscoverRoute(pathname)) {
		return (
			<PageMark>
				<CompassIcon className={styles.contextIcon} weight="fill" />
				<span className={styles.contextLabel}>{i18n._(EXPLORE_COMMUNITIES_DESCRIPTOR)}</span>
			</PageMark>
		);
	}

	if (Routes.isDMRoute(pathname)) {
		return (
			<PageMark>
				<LafonLoIcon className={styles.contextMark} />
				<span className={styles.contextLabel}>{i18n._(DIRECT_MESSAGES_DESCRIPTOR)}</span>
			</PageMark>
		);
	}

	if (pathname === Routes.BOOKMARKS) {
		return (
			<PageMark>
				<BookmarkSimpleIcon className={styles.contextIcon} weight="fill" />
				<span className={styles.contextLabel}>{i18n._(BOOKMARKS_DESCRIPTOR)}</span>
			</PageMark>
		);
	}

	if (pathname === Routes.MENTIONS) {
		return (
			<PageMark>
				<AtIcon className={styles.contextIcon} weight="bold" />
				<span className={styles.contextLabel}>{i18n._(RECENT_MENTIONS_DESCRIPTOR)}</span>
			</PageMark>
		);
	}

	if (pathname === Routes.NOTIFICATIONS) {
		return (
			<PageMark>
				<BellIcon className={styles.contextIcon} weight="fill" />
				<span className={styles.contextLabel}>{i18n._(UNREADS_DESCRIPTOR)}</span>
			</PageMark>
		);
	}

	if (pathname === Routes.YOU) {
		const user = Users.currentUser;
		if (!user) return null;
		return (
			<PageMark>
				<Avatar user={user} size={16} className={styles.contextAvatar} />
				<span className={styles.contextLabel}>{user.displayName}</span>
			</PageMark>
		);
	}

	if (pathname.startsWith('/users/')) {
		const userId = pathname.split('/')[2];
		const user = userId ? Users.getUser(userId) : undefined;
		if (!user) return null;
		return (
			<PageMark>
				<Avatar user={user} size={16} className={styles.contextAvatar} />
				<span className={styles.contextLabel}>{user.displayName}</span>
			</PageMark>
		);
	}

	if (pathname === Routes.STORE) {
		return (
			<PageMark>
				<StorefrontIcon className={styles.contextIcon} weight="fill" />
				<span className={styles.contextLabel}>{i18n._(STORE_COPY.title)}</span>
			</PageMark>
		);
	}

	if (pathname === Routes.PREMIUM) {
		return (
			<PageMark>
				<SparkleIcon className={styles.contextIcon} weight="fill" />
				<span className={styles.contextLabel}>{PREMIUM_PRODUCT_NAME}</span>
			</PageMark>
		);
	}

	if (pathname.startsWith('/lobbies')) {
		return (
			<PageMark>
				<GameControllerIcon className={styles.contextIcon} weight="fill" />
				<span className={styles.contextLabel}>{i18n._(LOBBY_COPY.title)}</span>
			</PageMark>
		);
	}

	if (pathname.startsWith('/discover')) {
		return (
			<PageMark>
				<CompassIcon className={styles.contextIcon} weight="fill" />
				<span className={styles.contextLabel}>{i18n._(SOCIAL_COPY.discover)}</span>
			</PageMark>
		);
	}

	return null;
});
